import { localDate } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import { X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const flows = ["light", "moderate", "heavy", "very_heavy"];
const periodLengths = [3, 4, 5, 6, 7, 8];
const cycleLengths = [21,22,23,24,25,26,27,28,29,30,31,32,33,34,35];

const OnboardingFlow = ({ onDone }) => {
  const { t } = useTranslation();
  const { token, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    last_period_start: "",
    period_length: 5,
    cycle_length: 28,
    flow_intensity: "moderate"
  });
  const [saving, setSaving] = useState(false);

  const submit = async (skipped = false) => {
    setSaving(true);
    try {
      await axios.post(`${API}/onboarding`, { ...data, skipped }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      await refreshUser?.();
      toast.success("You're all set!");
      onDone?.();
    } catch (e) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="bg-[#FFF8F5] rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden">
        <div className="p-6 border-b border-[#F2E4EB] flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C084A0] font-semibold">{step} / 4</p>
            <h2 className="font-heading text-2xl text-[#2E1D28] mt-1">{t("onboarding.title")}</h2>
          </div>
          <button onClick={() => submit(true)} className="text-[#8A7A85] hover:text-[#2E1D28]" data-testid="onboarding-skip">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 min-h-[240px]">
          {step === 1 && (
            <div>
              <label className="font-heading text-lg text-[#2E1D28] block mb-4">{t("onboarding.q1")}</label>
              <input
                type="date"
                value={data.last_period_start}
                max={localDate()}
                onChange={(e) => setData({ ...data, last_period_start: e.target.value })}
                className="w-full rounded-xl border border-[#F2E4EB] px-4 py-3 focus:outline-none focus:border-[#C084A0]"
                data-testid="onboarding-date"
              />
            </div>
          )}
          {step === 2 && (
            <div>
              <label className="font-heading text-lg text-[#2E1D28] block mb-4">{t("onboarding.q2")}</label>
              <div className="grid grid-cols-3 gap-2">
                {periodLengths.map(v => (
                  <button
                    key={v}
                    onClick={() => setData({ ...data, period_length: v })}
                    className={`rounded-xl border px-3 py-3 text-sm font-medium transition ${
                      data.period_length === v ? 'bg-[#C084A0] text-white border-[#C084A0]' : 'bg-white border-[#F2E4EB] text-[#2E1D28]'
                    }`}
                    data-testid={`onboarding-period-${v}`}
                  >
                    {v} {t("onboarding.days")}
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div>
              <label className="font-heading text-lg text-[#2E1D28] block mb-4">{t("onboarding.q3")}</label>
              <div className="grid grid-cols-5 gap-2 mb-4 max-h-40 overflow-y-auto">
                {cycleLengths.map(v => (
                  <button
                    key={v}
                    onClick={() => setData({ ...data, cycle_length: v })}
                    className={`rounded-xl border px-2 py-2 text-sm font-medium transition ${
                      data.cycle_length === v ? 'bg-[#C084A0] text-white border-[#C084A0]' : 'bg-white border-[#F2E4EB] text-[#2E1D28]'
                    }`}
                    data-testid={`onboarding-cycle-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setData({ ...data, cycle_length: 28 })}
                className="text-sm text-[#C084A0] underline"
                data-testid="onboarding-not-sure"
              >
                {t("onboarding.not_sure")}
              </button>
            </div>
          )}
          {step === 4 && (
            <div>
              <label className="font-heading text-lg text-[#2E1D28] block mb-4">{t("onboarding.q4")}</label>
              <div className="grid grid-cols-2 gap-2">
                {flows.map(v => (
                  <button
                    key={v}
                    onClick={() => setData({ ...data, flow_intensity: v })}
                    className={`rounded-xl border px-3 py-4 text-sm font-medium transition ${
                      data.flow_intensity === v ? 'bg-[#C084A0] text-white border-[#C084A0]' : 'bg-white border-[#F2E4EB] text-[#2E1D28]'
                    }`}
                    data-testid={`onboarding-flow-${v}`}
                  >
                    {t(`onboarding.flow_${v}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-6 pt-0 flex items-center justify-between">
          <Button variant="ghost" onClick={() => submit(true)} className="text-[#8A7A85]" data-testid="onboarding-skip-btn">
            {t("onboarding.skip")}
          </Button>
          <div className="flex gap-2">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-full">
                <ChevronLeft className="w-4 h-4 mr-1" strokeWidth={1.5} /> {t("onboarding.back")}
              </Button>
            )}
            {step < 4 && (
              <Button onClick={() => setStep(step + 1)} className="rounded-full bg-[#C084A0] hover:bg-[#A96B87] text-white" data-testid="onboarding-next">
                {t("onboarding.next")} <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
              </Button>
            )}
            {step === 4 && (
              <Button onClick={() => submit(false)} disabled={saving} className="rounded-full bg-[#C084A0] hover:bg-[#A96B87] text-white" data-testid="onboarding-finish">
                {saving ? "…" : t("onboarding.finish")} <Check className="w-4 h-4 ml-1" strokeWidth={1.5} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const useOnboardingCheck = () => {
  const { isAuthenticated, token } = useAuth();
  const [needed, setNeeded] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/onboarding/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!r.data?.completed) setNeeded(true);
      } catch (e) { /* silent */ }
    })();
  }, [isAuthenticated, token]);
  return [needed, setNeeded];
};

export default OnboardingFlow;
